<template>
    <div class="ds-dossier-body">
        <div class="ds-dossier-subhead">
            <button type="button" class="ds-dossier-back" @click="emit('back')">
                <svg width="7" height="11" viewBox="0 0 9 13" fill="currentColor" aria-hidden="true"><path d="M3.37716 6.5002L8.12091 11.244L6.76583 12.599L0.666992 6.5002L6.76583 0.401367L8.12091 1.75645L3.37716 6.5002Z" /></svg>
                {{ backLabel }}
            </button>
        </div>
        <div class="ds-dossier-section-head" style="border-bottom: 0; padding-bottom: 0">
            <span class="ds-dossier-subtitle">Все отклики</span>
            <span class="ds-dossier-muted">по всем подразделениям</span>
        </div>
        <!-- Фильтр периода общий с вкладкой «Биржа» и сохраняется при возврате. -->
        <div class="ds-dossier-chips" role="group" aria-label="Период">
            <button
                v-for="option in EXCHANGE_PERIODS"
                :key="option.id"
                type="button"
                class="ds-dossier-chip"
                :aria-pressed="period === option.id"
                @click="period = option.id"
            >{{ option.label }}</button>
        </div>

        <!-- GET /api/user/{id}/vacancy-responses/ — серверные страницы по 10 (count + results). -->
        <DossierState :status="pageState.status" :height="320" error-text="Не удалось загрузить отклики" @retry="retry" />
        <template v-if="pageState.status === 'ready'">
            <div v-if="!total && !pageItems.length" class="ds-dossier-state is-plain">Откликов за период нет</div>
            <template v-else>
                <DossierResponseList :responses="pageItems" />
                <div class="ds-dossier-pager">
                    <span>{{ rangeText }}</span>
                    <span v-if="pages > 1" style="display: flex; gap: 4px">
                        <button type="button" class="ds-dossier-icon-button" aria-label="Предыдущая страница" :disabled="page === 1" @click="page--">
                            <svg width="9" height="13" viewBox="0 0 9 13" fill="currentColor" aria-hidden="true"><path d="M3.37716 6.5002L8.12091 11.244L6.76583 12.599L0.666992 6.5002L6.76583 0.401367L8.12091 1.75645L3.37716 6.5002Z" /></svg>
                        </button>
                        <span style="align-self: center">{{ page }} из {{ pages }}</span>
                        <button type="button" class="ds-dossier-icon-button" aria-label="Следующая страница" :disabled="page === pages" @click="page++">
                            <svg width="9" height="13" viewBox="0 0 9 13" fill="currentColor" aria-hidden="true"><path d="M5.62363 6.5002L0.879883 1.75645L2.23497 0.401367L8.3338 6.5002L2.23497 12.599L0.879883 11.244L5.62363 6.5002Z" /></svg>
                        </button>
                    </span>
                </div>
            </template>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import type { Dayjs } from 'dayjs'
import { type ExchangePeriod, exchangeRange } from './dossierActivity'
import { type DossierData, RESPONSES_PAGE_SIZE } from './useDossierData'
import DossierResponseList from './DossierResponseList.vue'
import DossierState from './DossierState.vue'

const props = defineProps<{
    data: DossierData
    anchor: Dayjs
    end: Dayjs
    backLabel: string
}>()
const period = defineModel<ExchangePeriod>('period', { required: true })
const emit = defineEmits<{ back: [] }>()

const EXCHANGE_PERIODS: { id: ExchangePeriod, label: string }[] = [
    { id: '3m', label: '3 месяца' },
    { id: 'year', label: 'Год' },
    { id: 'all', label: 'Всё время' },
]

const range = computed(() => exchangeRange(period.value, props.anchor, props.end))
const page = ref(1)
watch(period, () => { page.value = 1 })

const query = computed(() => ({ range: range.value, page: page.value }))
watchEffect(() => props.data.responses.ensure(query.value))
const pageState = computed(() => props.data.responses.read(query.value))
const retry = () => props.data.responses.retry(query.value)

const total = computed(() => pageState.value.data?.count ?? 0)
const pages = computed(() => Math.max(1, Math.ceil(total.value / RESPONSES_PAGE_SIZE)))
const pageItems = computed(() => pageState.value.data?.results ?? [])
const rangeText = computed(() => {
    const start = (page.value - 1) * RESPONSES_PAGE_SIZE + 1
    return `${start}–${start + pageItems.value.length - 1} из ${total.value}`
})
</script>
